
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { BookingDetails } from './types';
import { useActivityLogger } from '@/hooks/useActivityLogger';

export function useBookingActions(
  setBookings: React.Dispatch<React.SetStateAction<BookingDetails[]>>,
  activeFilter: string
) {
  const { logActivity, ACTIVITY_EVENT_TYPES } = useActivityLogger();

  // Function to cancel a booking using the proper status update function
  const cancelBooking = useCallback(async (bookingId: string) => {
    try {
      console.log('[BOOKING-ACTIONS] Attempting to cancel booking:', bookingId);
      
      // Optimistic update - immediately update local state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, status: 'cancelled' } 
            : booking
        )
      );
      
      // Use the database function to properly update booking status
      const { data, error } = await supabase.rpc('update_booking_status', {
        p_booking_id: bookingId,
        p_new_status: 'cancelled',
        p_updated_by: null, // system update
        p_notes: 'Booking cancelled by customer'
      });
      
      if (error) {
        console.error('[BOOKING-ACTIONS] Error cancelling booking:', error);
        
        // Rollback optimistic update on error
        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.id === bookingId 
              ? { ...booking, status: 'new' } // Rollback to previous status
              : booking
          )
        );
        
        throw error;
      }

      console.log('[BOOKING-ACTIONS] Booking cancellation result:', data);
      
      toast({
        title: "Booking cancelled",
        description: "The booking has been cancelled successfully",
      });
      
      return true;
    } catch (error) {
      console.error('[BOOKING-ACTIONS] Error cancelling booking:', error);
      toast({
        title: "Error",
        description: "Failed to cancel the booking",
        variant: "destructive",
      });
      return false;
    }
  }, [setBookings]);

  return { cancelBooking };
}
